import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import uploadImage from "../../../assets/CreateCategory/upload.svg";
import dividerImage from "../../../assets/CreateCategory/divider.svg";
import "./style.scss";
import CustomDropZone from "../../../common/CustomDropZone";
import { useDropzone } from "react-dropzone";
import { useUpload } from "../../../context/uploadFileContext";
import Button from "../../../common/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackIcon from "../../../assets/Header/backIcon.svg";
import { message, Progress, Spin } from "antd";
import pauseIcon from "../../../assets/UploadImages/pauseIcon.svg";
import playIcon from "../../../assets/UploadImages/playIcon.svg";
import deleteIcon from "../../../assets/UploadImages/deleteIcon.svg";
import deleteIconGrey from "../../../assets/UploadImages/deleteIconGrey.svg";
import FolderIcon from "../../../assets/UploadImages/FolderIcon.svg";
import {
  useDeleteFolder,
  useGetBatchSummary,
} from "../../../hooks/useOrderBatches";
import { BatchMarketPlaceContext } from "../../../context/batchProvider";
import Stepper from "../../Stepper";

const UploadImages = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadFiles, setUploadFiles] = useState({});
  const {
    batch_setSelectedMarketPlaces,
    batch_setSelectedAddOnServices,
    batch_skipPages,
  } = useContext(BatchMarketPlaceContext);
  const {
    startUpload,
    pauseUpload,
    uploadedFolders,
    setUploadedFolders,
    startUploading,
    setStartUploading,
  } = useUpload();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const activeStep = useMemo(() => {
    const from = location.state?.from;
    if (
      batch_skipPages.includes("addOnService") &&
      batch_skipPages.includes("viewGuidelines") &&
      (from === "select-marketplace" || from === "sku-list")
    ) {
      return 5;
    } else if (from === "sequencing") {
      return 5;
    } else if (from === "add-on-services" || from === "sku-list") {
      return 5;
    } else {
      return 1;
    }
  }, [location.state, batch_skipPages]);
  const onSuccessBatchSummary = (res) => {
    if (res.status === 200) {
      let folderData = [];
      let addOndata = [];
      let marketPlaceData = [];
      if (res.data.data.uploads.length > 0) {
        res.data.data.uploads.forEach((upload) => {
          if (upload?.uploadData) {
            folderData.push({
              batchId: params.batchId,
              folderId: upload?._id,
              folderName: upload?.uploadData?.folderName,
              memory: {
                uploading: false,
                progress: 0,
                totalUploaded: 0,
                totalFiles: upload?.activeFileCount,
                totalMemory: upload?.uploadData?.foldersize,
                paused: false,
              },
            });
          } else {
            handleFolderDelete(upload?._id);
          }
        });

        const mergedFolders = [
          ...uploadedFolders.filter(
            (existingFolder) =>
              !folderData.some(
                (newFolder) => newFolder.folderId === existingFolder.folderId
              )
          ),
          ...folderData,
        ];

        setUploadedFolders(mergedFolders);
      }

      if (res.data.data?.batchAddOnServices?.length > 0) {
        res.data.data.batchAddOnServices.forEach((batch, index) => {
          addOndata.push({
            packageId: batch.package,
            serviceId: batch.service,
            variantId: batch.variant,
            comments: batch.comments,
          });
        });
        batch_setSelectedAddOnServices(addOndata);
      }
      if (res.data.data?.marketplaces?.length > 0) {
        res.data.data.marketplaces.forEach((marketplace, index) => {
          marketPlaceData.push({
            id: marketplace?._id,
            name: marketplace?.name,
          });
        });
        batch_setSelectedMarketPlaces(marketPlaceData);
      }
    }
  };
  const onErrorBatchSummary = () => {};
  const { data } = useGetBatchSummary(
    "batch_summary",
    params.batchId,
    onSuccessBatchSummary,
    onErrorBatchSummary
  );
  const onErrorDeleteFolder = (err) => {
    message.error(err);
  };
  const onSuccessDeleteFolder = (res, payload) => {
    if (res.status === 200) {
      let filteredData = uploadedFolders.filter((x) => {
        return x.folderId !== payload.uploadId;
      });
      setUploadedFolders(filteredData);
    }
  };
  const { mutate: deleteFolder } = useDeleteFolder(
    onSuccessDeleteFolder,
    onErrorDeleteFolder
  );

  const handleFolderDelete = async (folderId) => {
    try {
      pauseUpload(folderId);
      await new Promise((resolve) => setTimeout(resolve, 100));
      var payload = {
        uploadId: folderId,
        batch: params.batchId,
      };
      deleteFolder(payload);

      const fileInput = document.getElementById("file");
      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error("Error handling folder delete:", error);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/webp": [".webp"],
    },

    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadFiles((prev) => ({
          ...prev,
          categoryImage: acceptedFiles[0],
        }));
      }
    },
    multiple: true,
  });

  const onDropItems = useCallback(
    (files) => {
      const hasHiddenFile = files.some(
        (file) => file.relativePath && file.relativePath.startsWith(".")
      );

      if (hasHiddenFile) {
        message.error("Only folders are allowed");
        setIsDragging(false);
        return;
      }

      if (files.length > 0) {
        setUploadFiles((prev) => ({
          ...prev,
          categoryImage: files[0],
        }));
      }

      startUpload(files, params.batchId);

      setIsDragging(false);
    },
    [uploadFiles, params.batchId]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const renderDragAndDropSection = useCallback(
    () =>
      isDragging && (
        <CustomDropZone
          dropZoneClass={"uploaded_images_row"}
          onDropItems={onDropItems}
          isShowTextOnDrag={true}
          acceptingFormates={{
            "image/jpg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/jpeg": [".jpeg", ".jpg"],
            "image/webp": [".webp"],
          }}
          handleDragLeave={handleDragLeave}
          multiple={true}
        />
      ),
    [isDragging, onDropItems, handleDragLeave]
  );

  const handleUploadImage = useCallback(
    (files) => {
      if (files.length > 0) {
        setUploadFiles((prev) => ({
          ...prev,
          categoryImage: files[0],
        }));
      }
      setStartUploading(true);
      startUpload(files, params.batchId);
    },
    [uploadFiles]
  );

  const handleAreaClick = () => {
    document.getElementById("file").click();
  };
  const handleNextBtnClick = () => {
    const from = location.state?.from;
    if (
      from === "add-on-services" ||
      from === "sku-list" ||
      from === "sequencing" ||
      from === "select-marketplace"
    ) {
      navigate(`/sku-list/${params.batchId}`);
    } else {
      navigate(`/select-marketplace/${params.batchId}`);
    }
  };
  const handleBackBtnClick = () => {
    const from = location.state?.from;
    if (
      batch_skipPages.includes("addOnService") &&
      batch_skipPages.includes("viewGuidelines") &&
      (from === "select-marketplace" || from === "sku-list")
    ) {
      console.log("from", from);
      navigate(`/select-marketplace/${params?.batchId}`, {
        state: {
          from: "confirm-upload-images",
        },
      });
    } else if (from === "sequencing") {
      navigate(`/sequencing/${params.batchId}`);
      return;
    } else if (from === "add-on-services" || from === "sku-list") {
      navigate(`/add-on-services/${params.batchId}`);
    } else {
      navigate(`/order-list`);
    }
  };
  const getUploadSection = () => {
    return (
      <div className="upload_wrapper">
        <div className="category_image">
          <img src={uploadImage} alt="" />
          <p>Drag your folder</p>
        </div>
        <div className="or_image">
          <img src={dividerImage} alt="" />
        </div>
        <label className="browse">Browse Folders</label>
        <input
          type="file"
          accept="image/png,image/jpg,image/jpeg, image/webp"
          id="file"
          onChange={(e) => handleUploadImage(e.target.files)}
          //onClick={(e) => (e.target.value = null)}
          className="upload_more_images_btn_input"
          multiple
          webkitdirectory="true"
        />
      </div>
    );
  };
  const checkBtnDisabled = () => {
    const from = location.state?.from;
    if (
      from === "add-on-services" ||
      from === "sku-list" ||
      from === "select-marketplace" ||
      from === "sequencing"
    ) {
      return uploadedFolders?.some((folder) => folder.memory.uploading) ||
        uploadedFolders?.length === 0
        ? true
        : false;
    } else {
      return false;
    }
  };
  return (
    <div className="upload_images" onDragEnter={handleDragEnter}>
      <div className="upload_box">
        <div className="uploading_container">
          <div className="text_section">
            <h2 className="upload_title">Upload Images</h2>
            <span>Only Folders are allowed.</span>
          </div>
          <div className="item_upload_wrapper" onClick={handleAreaClick}>
            {getUploadSection()}
            {renderDragAndDropSection()}
          </div>
          <div className="extensions">
            <p className="extension_text">
              Only .jpg and .png files are supported. Please upload them inside
              a folder
            </p>
          </div>
        </div>
        {uploadedFolders.length > 0 && (
          <div className="folder_container">
            {uploadedFolders.length > 0 &&
              uploadedFolders.map((folder, index) => {
                return !folder.memory.uploading ? (
                  <div className="uploaded_folder_wrapper" key={index}>
                    <div className="left_container">
                      <div className="folder_wrapper">
                        <img src={FolderIcon} alt="folderIcon" />
                      </div>
                      <div className="text_wrapper">
                        <div className="folder_name">{folder.folderName}</div>
                        <div className="memory">
                          {folder.memory.totalMemory}MB
                        </div>
                      </div>
                    </div>
                    <div className="right_wrapper">
                      <img
                        src={deleteIconGrey}
                        alt="deleteIconGrey"
                        onClick={() => handleFolderDelete(folder.folderId)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="uploading_wrapper">
                    <div className="content_wrapper">
                      <div className="text_wrapper">
                        <div className="uploaded">
                          {" "}
                          {folder.memory.progress}% Uploaded
                        </div>
                        <div className="uploding_numbers">
                          {" "}
                          {folder.memory.totalUploaded}/
                          {folder.memory.totalFiles} images
                        </div>
                      </div>
                      <div className="action">
                        <div className="stop_icon_wrapper">
                          <img
                            src={deleteIcon}
                            alt="deleteIcon"
                            onClick={() => handleFolderDelete(folder.folderId)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="progress_wrapper">
                      <Progress
                        percent={folder.memory.progress}
                        status="active"
                        format={() => null}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        {startUploading && (
          <div className="loading_container">
            {" "}
            <Spin size="large" />{" "}
          </div>
        )}
      </div>
      <div className="bottom_panel">
        <Stepper activeStep={activeStep} isSeggregation={false} />

        <div className="action_wrapper">
          <button className="backBtn" onClick={handleBackBtnClick}>
            <img src={BackIcon} alt="backBtn" />
          </button>
          <Button
            text="Next"
            width="140px"
            height="50px"
            handleClick={handleNextBtnClick}
            isDisabled={checkBtnDisabled()}
          />
        </div>
      </div>
    </div>
  );
};

export default UploadImages;
